import React, { useState, useEffect, useRef } from 'react';
import './ActiveCall.css';

function VideoCall({ callerInfo, videoUrl, audioUrls, onCallEnd }) {
  const [callDuration, setCallDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    // Start timer
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current && videoLoaded) {
      videoRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted, videoLoaded]);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    // Auto-play when loaded
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.error('Video autoplay failed:', err);
          setVideoError('Tap to play video');
        });
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setTimeout(() => onCallEnd(), 1000);
  };

  const handleVideoError = (e) => {
    console.error('Video error:', e);
    setVideoError('Failed to load video');
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(err => console.error('Play failed:', err));
    }
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (videoRef.current) {
      videoRef.current.volume = newMutedState ? 0 : volume;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return '🔇';
    if (volume < 0.5) return '🔉';
    return '🔊';
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallerName = () => {
    switch(callerInfo.callerType) {
      case 'SANTA':
        return 'Santa';
      case 'ELF':
      case 'ELF_MALE':
        return 'Jingle Jack';
      case 'ELF_FEMALE':
        return 'Snowdrop Ellie';
      case 'MRS_CLAUS':
        return 'Mrs. Claus';
      case 'GRANDPA_JOE':
        return 'Santa';
      default:
        return 'Santa';
    }
  };

  return (
    <div className="active-call video-call">
      <div className="call-header">
        <h2 className="caller-name-active">{getCallerName()}</h2>
        <p className="call-timer">{formatDuration(callDuration)}</p>
      </div>

      <div className="video-container">
        {!videoLoaded && !videoError && (
          <div className="video-loading">
            <div className="loading-spinner"></div>
            <p>Loading video...</p>
          </div>
        )}

        {videoError && (
          <div className="video-error" onClick={togglePlay}>
            <p>{videoError}</p>
          </div>
        )}

        <video
          ref={videoRef}
          src={videoUrl}
          className={`call-video ${videoLoaded ? 'loaded' : ''}`}
          onLoadedData={handleVideoLoaded}
          onEnded={handleVideoEnded}
          onError={handleVideoError}
          playsInline
          onClick={togglePlay}
        />
      </div>

      <div className="call-controls">
        <button
          className={`control-btn ${isMuted ? 'muted' : ''}`}
          onClick={toggleMute}
        >
          <span className="control-icon">{isMuted ? '🔇' : '🎤'}</span>
          <span className="control-label">{isMuted ? 'unmute' : 'mute'}</span>
        </button>

        <div className="volume-control-container">
          <button
            className="control-btn"
            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
          >
            <span className="control-icon">{getVolumeIcon()}</span>
            <span className="control-label">speaker</span>
          </button>
          {showVolumeSlider && (
            <div className="volume-slider-container">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          )}
        </div>

        <button className="control-btn end-call-btn" onClick={onCallEnd}>
          <span className="control-icon">📞</span>
        </button>
      </div>
    </div>
  );
}

export default VideoCall;
